import type {MouseEvent as ReactMouseEvent} from 'react';
import {useState} from 'react';

import {useOutsideClick} from '../../../hooks/useOutsideClick';
import {useSelectList} from '../hooks/useSelectList';

/**
 * Represents a selectable option used in components like dropdowns, select inputs, or radio buttons.
 * Each option consists of a `label` that is visible to the user and a `value` that is used internally
 * to represent the selection in the application's logic. The `value` can be of any data type, making
 * the interface flexible to work with various kinds of data models.
 */
export interface Option {
    /**
     * The text displayed to the user in the selection component.
     */
    label: string;
    /**
     * The internal value associated with the option.
     */
    value: any;
}

/**
 * Custom hook for managing the state and behavior of a filter select input component.
 * This hook handles opening and closing the select dropdown, managing the selected value,
 * and triggering callbacks when the value changes. It also determines the position of the dropdown list
 * and whether the list should open above or below the select input.
 *
 * @param value      The initial value of the filter select.
 * @param options    The array of available options for the filter select. Each option includes a label and value.
 * @param onChange   Callback function to handle when the selected value changes.
 * @param isDisabled A boolean flag that determines whether the filter select is disabled.
 * @returns An object containing state and functions to control the behavior of the filter select input.
 *
 * @example
 * ```tsx
 * const { choosedOption, handleChangeValue, handleOpenSelect, isOpen, selectRef } = useFilterSelect(
 *   'initialValue',
 *   options,
 *   (newValue) => console.log(newValue),
 *   false
 * );
 * ```
 */
export const useFilterSelect = (value: any, options: Option[], onChange: (value: any) => void, isDisabled: boolean) => {
    const [selectedValue, setSelectedValue] = useState(value);
    const [isOpen, setIsOpen] = useState(false);
    const selectRef = useOutsideClick<HTMLDivElement>(setIsOpen);
    const {isTop, listHeight} = useSelectList(selectRef, isOpen);

    const chosenOption = options.filter(
        ({value: optionValue}) => optionValue === selectedValue
    )[0]?.label || '';

    /**
     * Handles the action of opening the select dropdown.
     * If the select input is disabled, the function returns early and does not open the dropdown.
     * Otherwise, it sets the dropdown to be open by updating the `isOpen` state to `true`.
     * This function is used to trigger the display of the options list when the select input is clicked.
     */
    const handleToggleSelect = () => {
        if (isDisabled) return;

        setIsOpen(!isOpen);
    };

    /**
     * Handles the action of selecting a new value from the dropdown options.
     * It stops the event propagation to prevent any parent click handlers from being triggered.
     * Then, it retrieves the selected value using the `index` stored in the dataset of the clicked option element.
     * Once the value is identified, it updates the state with the selected value, closes the dropdown, and triggers the `onChange` callback with the new value.
     *
     * @param event The mouse event triggered when an option is clicked.
     */
    const handleChangeValue = (event: ReactMouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        const {index: fieldValue} = event.currentTarget.dataset;
        const realValue = options.filter(({value: optionValue}) => String(optionValue) === fieldValue)[0]?.value;

        setSelectedValue(realValue);
        setIsOpen(false);
        onChange(realValue);
    };

    return {
        chosenOption,
        handleChangeValue,
        handleToggleSelect,
        isOpen,
        isTop,
        listHeight,
        selectRef
    };
};